import {
  NUMBER_OF_LEVELS,
  REFERRALS_IN_LEVEL,
  getUserLevel,
  getUserProgress,
} from "Dashboard";
import { useEffect, useState } from "react";
import { types } from "./ReferralForm/ReferralForm.common";
import { format } from "date-fns";

const SUCCESS_STATUS_OFF_DELAY_IN_MS = 3000;

/** @type {"disabled"|"idle"|"error"|"success"} */
const defaultState = "disabled";

export const useReferralButton = ({
  data,
  setError,
  setNewLevel,
  setNewProgress,
  setNewCountsForUpdate,
  setLevelProgress,
  originator,
  setNewReferral,
}) => {
  const dashboardAccessToken = originator?.dashboardAccessToken ?? "";
  const [state, setState] = useState(defaultState);
  const [selectedSenders, setSelectedSenders] = useState([]);
  const [accessToken, { recipient }] = Object.entries(data)[0] ?? [];
  const buttonId = Object.keys(data)[0] ?? "";
  const providers = data[buttonId]?.providers ?? [];
  const isSendersSelected =
    selectedSenders.filter(({ id }) => id !== originator?.id).length > 0;
  const [isReferralSending, setIsReferralSending] = useState(false);

  const createNewReferral = () => {
    /** @type {import("types").NewPatientFormData} */
    const newReferral = {
      buttonId,
      type: types.Notification,
      recipientName: recipient?.name ?? "",
      question: "",
      patientName: "",
      patientPhone: "",
      patientDob: "",
      location: "",
      gender: "",
      urgency: "",
      reason: "",
      dashboardAccessToken: dashboardAccessToken,
      providers,
    };

    setNewReferral(newReferral);
  };

  const handleReferralButtonClick = async (values, showSuccessNotification) => {
    // remove time from date to avoid timezone issues
    if (values?.patientDob) {
      values.patientDob = format(new Date(values.patientDob), "yyyy-MM-dd");
    }

    const formData = new FormData();
    formData.append("accessToken", accessToken);
    formData.append("dashboardAccessToken", dashboardAccessToken);
    formData.append("type", values.type);

    for (const key of Object.keys(values)) {
      if (key === "selectedFiles" && Array.isArray(values[key])) {
        const fileContents = values?.selectedFiles ?? [];
        fileContents.forEach((file) => {
          formData.append("selectedFiles[]", file);
        });
      } else if (Array.isArray(values[key])) {
        values[key].forEach((item) => {
          formData.append(`${key}[]`, JSON.stringify(item));
        });
      } else {
        formData.append(key, values[key]);
      }
    }

    setIsReferralSending(true);

    const createReferral = async () => {
      if (!accessToken) {
        return;
      }

      try {
        setState("disabled");

        // const isOnLine = await waitUntilDbIsOnline();

        // if (!isOnLine) {
        //   setError("No connection to cloud. Try again later.");
        //   setState("error");
        //   return;
        // }
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/create_referral`,
          {
            method: "POST",
            body: formData,
          }
        );
        const result = await response.json();

        if (result?.error || result?.message) {
          setError(result?.message || result?.error);
          setState("error");
          return;
        }

        if (!result?.isReferralCreated) {
          setError("Something went wrong. Referral was not created.");
          setState("error");
          return;
        }
        const referralCount = result?.referralCount ?? 0;
        setState("success");

        if (referralCount > 0) {
          if (
            referralCount > 0 &&
            referralCount / REFERRALS_IN_LEVEL <= NUMBER_OF_LEVELS &&
            referralCount % REFERRALS_IN_LEVEL === 0
          ) {
            setNewCountsForUpdate(referralCount);
            setLevelProgress(100);
            // inform about new level
            const userLevel = getUserLevel(getUserProgress(referralCount));
            setNewLevel(userLevel);
          } else {
            setNewProgress(referralCount);
          }
        }

        showSuccessNotification();
        setTimeout(() => {
          setState("idle");
        }, SUCCESS_STATUS_OFF_DELAY_IN_MS);
      } catch (err) {
        setState("error");
        setError(err?.message ?? "Something went wrong. Call support");
      } finally {
        setIsReferralSending(false);
      }
    };

    createReferral();
  };

  useEffect(() => {
    const currentSenders = data[Object.keys(data)[0]]?.senders ?? [];
    setSelectedSenders(currentSenders);
  }, [data]);

  useEffect(() => {
    setTimeout(() => setState("idle"), 300);
  }, []);

  return {
    state,
    createNewReferral,
    handleReferralButtonClick,
    recipient,
    accessToken,
    providers,
    isSendersSelected,
    isReferralSending,
    setIsReferralSending,
  };
};
